// MailContextMenu.tsx — Rechtsklick-Kontextmenü auf Mail-Listeneinträgen.
// Cluster POSTFACH (G1/G2/G3): ganz oben stehen die drei Bearbeitungs-Wege
// „Vorgang erstellen", „Einem Vorgang zuweisen…" und „Kontakt zuweisen…" — sie
// laufen über dieselbe Erkennungs-, Anhang- und Archivierungslogik wie das
// Mail-Detailfenster (mailListActions.ts). Darunter wie bisher die
// Ablage-Aktionen: „In Posteingang verschieben", „Verschieben nach…" (Ordnerbaum des
// Postfachs als Sub-Modus), „Archivieren", „Löschen" und „Als gelesen/ungelesen
// markieren". „Löschen" ist ein Verschieben nach `deleteditems` — genau wie
// „Archivieren" ein Verschieben nach `archive` ist. Die Mail landet also im
// Papierkorb und bleibt wiederherstellbar; es gibt bewusst kein Hard-Delete.
// Gerendert als fixed-Portal an der Mausposition (Muster: TerminContextMenu) mit
// Viewport-Klemmung, Außenklick/Escape zum Schließen. Theme-aware über Fluent-
// Tokens. Der Move nutzt den generischen Backend-Endpoint POST
// /mail/messages/:id/move (Well-Known-Name `inbox` bzw. opake Ordner-Id).

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import {
    Archive16Regular,
    ChevronLeft16Regular,
    Checkmark16Regular,
    Delete16Regular,
    ChevronRight16Regular,
    Folder16Regular,
    FolderArrowRight16Regular,
    FolderOpen16Regular,
    Mail16Regular,
    MailInbox16Regular,
    Filter16Regular,
    LinkSquare16Regular,
    MailRead16Regular,
    PersonAdd16Regular,
    TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import type { Email, MailboxAccess, MailFolder } from "../../services/graphMailService";
// Gemeinsam mit der Sidebar — sonst driften die beiden Ordnerdarstellungen
// auseinander. Seit Cluster POSTFACH (G5) ist das nicht nur eine Absicht,
// sondern dieselbe Funktion: `buildMailFolderOrder` bestimmt hier wie dort
// Struktur UND Reihenfolge.
import { buildMailFolderOrder, type MailFolderEntry } from "./mailFolderTree";
// E02-Zusatz: das Ziel ist auch postfachübergreifend wählbar.
import {
    buildMoveTarget,
    moveTargetMailboxes,
    type MoveTarget,
} from "./mailMoveTargets";
import { mailboxChipLabel } from "./mailSearch";
import { INK, INK2, LINE } from "../chat/chatTheme";
import { alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/** Ein offenes Kontextmenü: Mail + Quell-Postfach + Mausposition (Viewport). */
export interface MailCtxMenu {
    x: number;
    y: number;
    email: Email;
    mailbox: string;
}

interface Props {
    menu: MailCtxMenu | null;
    /**
     * Ordnerbäume je Postfach (aus MailContext.folderTrees). Es sind alle
     * lesbaren Postfächer, weil das Ziel postfachübergreifend wählbar ist (E02).
     */
    folderTrees: Record<string, MailFolder[]>;
    /** Lesbare Postfächer — Grundlage der Ziel-Postfach-Auswahl. */
    mailboxes: MailboxAccess[];
    /** Aktuelle Ansicht ist bereits der Posteingang → „In Posteingang verschieben" ausblenden. */
    hideInbox?: boolean;
    /** Aktuelle Ansicht ist bereits der Papierkorb → „Löschen" ausblenden (waere ein No-Op). */
    hideDelete?: boolean;
    /** G1 — „Vorgang erstellen" (mit Zuweisungs-Vorschritt, wenn kein Kontakt erkannt ist). */
    onVorgangErstellen: () => void;
    /** G2 — „Einem Vorgang zuweisen…" (derselbe Picker wie aus der geöffneten Mail). */
    onVorgangZuweisen: () => void;
    /** G3 — „Kontakt zuweisen…" (Zuweisen-Popup, danach archivieren, E03). */
    onKontaktZuweisen: () => void;
    /**
     * T-180 — „Einem Schaden zuordnen…": die Mail samt Anhängen in einen
     * Schaden-Ordner der Kundenakte legen, aus jedem Postfach. In schaden@
     * entfällt der Eintrag, dort sind die Vorgangszeilen schon diese Wege.
     */
    onSchadenZuordnen: () => void;
    /**
     * G4 — „Regel erstellen…": öffnet das Regel-Fenster, vorbelegt aus Absender,
     * Betreff und Postfach der angeklickten Mail (Cluster REGELN).
     */
    onCreateRule: () => void;
    /**
     * Für DIESE Mail läuft bereits eine Aktion (Erkennung/Anhänge unterwegs) →
     * die drei neuen Einträge sind gesperrt, damit kein zweiter Ablauf startet.
     */
    busy?: boolean;
    onMoveToInbox: () => void;
    /**
     * Gewähltes Ziel aus dem Untermenü: Ordner plus Postfach. Bei
     * `crossMailbox === false` ist es der gewohnte Graph-Move.
     */
    onMoveTo: (target: MoveTarget) => void;
    onArchive: () => void;
    onDelete: () => void;
    onToggleRead: () => void;
    onClose: () => void;
}

// WICHTIG: Das Menü wird als Portal an document.body gerendert — also AUSSERHALB
// des FluentProviders. Die `tokens.*`-CSS-Variablen (--colorNeutralBackground1 …)
// sind dort NICHT definiert und lösen zu leer auf (transparentes, randloses Menü).
// Deshalb konkrete Design-Farben (kvp-rd/Chat-Palette) statt Tokens.
const useStyles = makeStyles({
    menu: {
        position: "fixed",
        display: "flex",
        flexDirection: "column",
        minWidth: "236px",
        maxWidth: "320px",
        maxHeight: "calc(100vh - 16px)",
        padding: "4px",
        backgroundColor: "#ffffff",
        border: `1px solid ${LINE}`,
        borderRadius: "8px",
        boxShadow: "0 8px 28px rgba(36, 21, 34, 0.18)",
        zIndex: 100000,
        fontSize: "13px",
        color: INK,
    },
    item: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        width: "100%",
        padding: "8px 10px",
        border: "none",
        background: "transparent",
        color: "inherit",
        cursor: "pointer",
        textAlign: "left",
        borderRadius: "6px",
        fontSize: "13px",
        lineHeight: "18px",
        flexShrink: 0,
        ":hover": { backgroundColor: "#f4eff6" },
        ":disabled": { opacity: 0.5, cursor: "default", backgroundColor: "transparent" },
    },
    // Löschen bewusst abgesetzt: die Aktion sitzt direkt neben „Archivieren",
    // sieht dort sonst identisch aus und ist die einzige mit Papierkorb-Folge.
    danger: { color: "#c23b3b", ":hover": { backgroundColor: "#fbecec" } },
    grow: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    chevron: { color: INK2, flexShrink: 0 },
    divider: {
        height: "1px",
        backgroundColor: LINE,
        margin: "4px 6px",
        flexShrink: 0,
    },
    subHeader: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        padding: "6px 8px",
        border: "none",
        background: "transparent",
        color: INK2,
        cursor: "pointer",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 600,
        flexShrink: 0,
        ":hover": { backgroundColor: "#f4eff6" },
    },
    folderList: {
        maxHeight: "min(60vh, 360px)",
        overflowY: "auto",
        overscrollBehavior: "contain",
    },
    countBadge: {
        color: INK2,
        fontSize: "11px",
        flexShrink: 0,
    },
    empty: {
        padding: "10px",
        color: INK2,
        fontSize: "12px",
    },
});

/** Ordnerbaum in eine flache Liste mit Tiefen-Einrückung überführen. */
function clampToViewport(x: number, y: number, w: number, h: number) {
    const pad = 8;
    const left = Math.max(pad, Math.min(x, aktivesFenster().innerWidth - w - pad));
    const top = Math.max(pad, Math.min(y, aktivesFenster().innerHeight - h - pad));
    return { top, left };
}

export const MailContextMenu: React.FC<Props> = ({
    menu,
    folderTrees,
    mailboxes,
    hideInbox,
    hideDelete,
    onVorgangErstellen,
    onVorgangZuweisen,
    onKontaktZuweisen,
    onSchadenZuordnen,
    onCreateRule,
    busy,
    onMoveToInbox,
    onMoveTo,
    onArchive,
    onDelete,
    onToggleRead,
    onClose,
}) => {
    const styles = useStyles();
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const [mode, setMode] = useState<"menu" | "move" | "move-mailbox">("menu");
    // Ziel-Postfach des Verschieben-Untermenüs (E02-Zusatz). Fällt bei jedem
    // neuen Menü auf das Postfach der angeklickten Mail zurück.
    const [moveMailbox, setMoveMailbox] = useState<string>("");

    // Bei jedem neuen Menü zurück in den Wurzel-Modus.
    useEffect(() => {
        if (menu) {
            setMode("menu");
            setMoveMailbox(menu.mailbox);
        }
    }, [menu]);

    // Position messen + klemmen (auch beim Modus-Wechsel — die Höhe ändert sich).
    useLayoutEffect(() => {
        if (!menu) {
            setPos(null);
            return;
        }
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos(clampToViewport(menu.x, menu.y, r.width, r.height));
    }, [menu, mode]);

    // Außenklick / Escape / Scroll / Resize schließen.
    useEffect(() => {
        if (!menu) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current?.contains(e.target as Node)) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        const onResize = () => onClose();
        // Scroll schließt das Menü — AUSSER der Scroll passiert IM Menü selbst
        // (sonst würde das Scrollen der Ordnerliste das Menü sofort schließen).
        const onScroll = (e: Event) => {
            if (ref.current?.contains(e.target as Node)) return;
            onClose();
        };
        alleFenster.addEventListener("pointerdown", onDown);
        alleFenster.addEventListener("keydown", onKey);
        alleFenster.addEventListener("resize", onResize);
        alleFenster.addEventListener("scroll", onScroll, true);
        return () => {
            alleFenster.removeEventListener("pointerdown", onDown);
            alleFenster.removeEventListener("keydown", onKey);
            alleFenster.removeEventListener("resize", onResize);
            alleFenster.removeEventListener("scroll", onScroll, true);
        };
    }, [menu, onClose]);

    if (!menu) return null;
    const read = menu.email.read;
    const zielPostfach = moveMailbox || menu.mailbox;
    const baum = folderTrees[zielPostfach] ?? [];
    // Struktur und Reihenfolge der linken Postfach-Navigation (E02): die fünf
    // Standard-Ordner fest oben, ihre Unterordner eingerückt darunter, dann die
    // eigenen Ordner gleichrangig. Nichts wird mehr alphabetisch gemischt.
    const eintraege = buildMailFolderOrder(baum, { includeCustom: true });
    const zielPostfaecher = moveTargetMailboxes(mailboxes, menu.mailbox);
    // Schadenpostfach: dieselben zwei Einträge, aber mit dem Schaden-Ordner als Ziel.
    const istSchadenPostfach = mailboxes.some(
        (m) => m.schadenAblage === true && m.mailboxAddress.toLowerCase() === menu.mailbox.toLowerCase(),
    );
    // Die Postfach-Zeile erscheint nur, wenn es überhaupt etwas zu wählen gibt.
    const postfachWaehlbar = zielPostfaecher.length > 1;

    const run = (fn: () => void) => {
        fn();
        onClose();
    };

    return createPortal(
        <div
            ref={ref}
            className={styles.menu}
            role="menu"
            aria-label="E-Mail-Aktionen"
            style={{
                top: pos?.top ?? -9999,
                left: pos?.left ?? -9999,
                visibility: pos ? "visible" : "hidden",
            }}
        >
            {mode === "menu" ? (
                <>
                    {/* Cluster POSTFACH (G1/G2/G3) — dieselben Wege wie aus der
                        geöffneten Mail, nur ohne sie zu öffnen. Darunter
                        „Regel erstellen…" (Cluster REGELN, G4). */}
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.item}
                        disabled={busy}
                        aria-disabled={busy || undefined}
                        onClick={() => run(onVorgangErstellen)}
                    >
                        <TaskListSquareLtr16Regular aria-hidden />
                        <span className={styles.grow}>{istSchadenPostfach ? "Neuer Schaden" : "Vorgang erstellen"}</span>
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.item}
                        disabled={busy}
                        aria-disabled={busy || undefined}
                        onClick={() => run(onVorgangZuweisen)}
                    >
                        <LinkSquare16Regular aria-hidden />
                        <span className={styles.grow}>
                            {istSchadenPostfach ? "Einem bestehenden Schaden zuordnen…" : "Einem Vorgang zuweisen…"}
                        </span>
                    </button>
                    {/* T-180 (28.10.2026): Das Schaden-Team legt Mails übergangsweise
                        in die Schaden-Ordner der Kundenakte — aus jedem Postfach,
                        nicht nur aus schaden@. */}
                    {!istSchadenPostfach && (
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.item}
                            disabled={busy}
                            aria-disabled={busy || undefined}
                            onClick={() => run(onSchadenZuordnen)}
                        >
                            <FolderOpen16Regular aria-hidden />
                            <span className={styles.grow}>Einem Schaden zuordnen…</span>
                        </button>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.item}
                        disabled={busy}
                        aria-disabled={busy || undefined}
                        onClick={() => run(onKontaktZuweisen)}
                    >
                        <PersonAdd16Regular aria-hidden />
                        <span className={styles.grow}>Kontakt zuweisen…</span>
                    </button>
                    <div className={styles.divider} />
                    {/* G4 — nicht an `busy` gekoppelt: das Regel-Fenster startet
                        keinen Ablauf an DIESER Mail, es liest nur ihre Eckdaten
                        als Vorbelegung. */}
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.item}
                        onClick={() => run(onCreateRule)}
                    >
                        <Filter16Regular aria-hidden />
                        <span className={styles.grow}>Regel erstellen…</span>
                    </button>
                    <div className={styles.divider} />
                    {!hideInbox && (
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.item}
                            onClick={() => run(onMoveToInbox)}
                        >
                            <MailInbox16Regular aria-hidden />
                            <span className={styles.grow}>In Posteingang verschieben</span>
                        </button>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.item}
                        onClick={() => setMode("move")}
                    >
                        <FolderArrowRight16Regular aria-hidden />
                        <span className={styles.grow}>Verschieben nach…</span>
                        <ChevronRight16Regular aria-hidden className={styles.chevron} />
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.item}
                        onClick={() => run(onArchive)}
                    >
                        <Archive16Regular aria-hidden />
                        <span className={styles.grow}>Archivieren</span>
                    </button>
                    {!hideDelete && (
                        <button
                            type="button"
                            role="menuitem"
                            className={mergeClasses(styles.item, styles.danger)}
                            onClick={() => run(onDelete)}
                        >
                            <Delete16Regular aria-hidden />
                            <span className={styles.grow}>Löschen</span>
                        </button>
                    )}
                    <div className={styles.divider} />
                    <button
                        type="button"
                        role="menuitem"
                        className={styles.item}
                        onClick={() => run(onToggleRead)}
                    >
                        {read ? <Mail16Regular aria-hidden /> : <MailRead16Regular aria-hidden />}
                        <span className={styles.grow}>
                            {read ? "Als ungelesen markieren" : "Als gelesen markieren"}
                        </span>
                    </button>
                </>
            ) : mode === "move-mailbox" ? (
                <>
                    <button
                        type="button"
                        className={styles.subHeader}
                        onClick={() => setMode("move")}
                    >
                        <ChevronLeft16Regular aria-hidden /> Ziel-Postfach
                    </button>
                    <div className={styles.divider} />
                    <div className={styles.folderList}>
                        {zielPostfaecher.map((mb) => (
                            <button
                                key={mb.mailboxAddress}
                                type="button"
                                role="menuitem"
                                className={styles.item}
                                onClick={() => {
                                    setMoveMailbox(mb.mailboxAddress);
                                    setMode("move");
                                }}
                            >
                                <Mail16Regular aria-hidden />
                                <span className={styles.grow}>
                                    {mb.displayName || mb.mailboxAddress}
                                </span>
                                {mb.mailboxAddress.toLowerCase() ===
                                    zielPostfach.toLowerCase() && (
                                    <Checkmark16Regular aria-hidden className={styles.chevron} />
                                )}
                            </button>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <button
                        type="button"
                        className={styles.subHeader}
                        onClick={() => setMode("menu")}
                    >
                        <ChevronLeft16Regular aria-hidden /> Verschieben nach
                    </button>
                    <div className={styles.divider} />
                    {postfachWaehlbar && (
                        <>
                            {/* E02-Zusatz: Ziel-Postfach umschalten (persönliches
                                Postfach oder CBB). Serverseitig ist das dann kein
                                Graph-Move mehr, sondern eine MIME-Kopie ins Ziel
                                plus Papierkorb in der Quelle. */}
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.item}
                                onClick={() => setMode("move-mailbox")}
                            >
                                <Mail16Regular aria-hidden />
                                <span className={styles.grow}>
                                    {mailboxChipLabel(zielPostfach)}
                                </span>
                                <ChevronRight16Regular aria-hidden className={styles.chevron} />
                            </button>
                            <div className={styles.divider} />
                        </>
                    )}
                    <div className={styles.folderList}>
                        {eintraege.map((entry) => (
                            <button
                                key={entry.key}
                                type="button"
                                role="menuitem"
                                className={styles.item}
                                style={{ paddingLeft: `${10 + entry.depth * 16}px` }}
                                onClick={() =>
                                    run(() =>
                                        onMoveTo(buildMoveTarget(menu.mailbox, entry, zielPostfach)),
                                    )
                                }
                            >
                                {entry.alias === "inbox" ? (
                                    <MailInbox16Regular aria-hidden />
                                ) : (
                                    <Folder16Regular aria-hidden />
                                )}
                                <span className={styles.grow}>{entry.label}</span>
                                {entry.unread > 0 && (
                                    <span className={styles.countBadge}>{entry.unread}</span>
                                )}
                            </button>
                        ))}
                        {/* Die fünf Standardzeilen stehen immer da (gültige
                            Well-Known-Ziele) — leer kann die Liste also nie
                            sein. Fehlt der Baum noch, fehlen nur die eigenen
                            Ordner, und genau das sagt der Hinweis. */}
                        {baum.length === 0 && (
                            <div className={styles.empty}>Weitere Ordner werden geladen…</div>
                        )}
                    </div>
                </>
            )}
        </div>,
        document.body,
    );
};

export default MailContextMenu;
